import { isIgnoredClientError, isThirdPartyStack } from "./errorNoise";

// Handled by src/server/routes/app-diag.post.ts, which forwards to Telegram.
// Not under /api (that prefix is proxied to the backend), and the path avoids
// "log"/"error", which ad-blocker lists match.
const REPORT_URL = "/app-diag";

// A render loop can throw hundreds of times a second, so each distinct error is
// sent once per page load, and only so many distinct ones.
const MAX_REPORTS_PER_PAGE = 10;

let listening = false;
const reported = new Set<string>();

interface ErrorDetails {
  message: string;
  name?: string;
  stack?: string;
  status?: number;
}

function describeError(error: unknown): ErrorDetails {
  if (error instanceof Error) {
    // ApiError (src/services/api.ts) carries the HTTP status of the failed call.
    const status = (error as { status?: unknown }).status;
    return {
      name: error.name,
      message: error.message || error.name,
      stack: error.stack,
      status: typeof status === "number" ? status : undefined,
    };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

// The trailing slash matters: "https://lotin.uz.evil.com" starts with our origin.
function isOwnUrl(url: string): boolean {
  return url.startsWith(`${window.location.origin}/`);
}

/**
 * Send an error to the team's Telegram group. Safe to call anywhere: it does
 * nothing during SSR and never throws.
 *
 * `context` names where the error was caught ("RouteError", "Translate", ...).
 */
export function reportError(
  context: string,
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  try {
    const details = describeError(error);
    if (
      isIgnoredClientError(details) ||
      isThirdPartyStack(details.stack, window.location.origin)
    ) {
      return;
    }

    const key = `${context}:${details.message}`;
    if (reported.has(key) || reported.size >= MAX_REPORTS_PER_PAGE) return;
    reported.add(key);

    const body = JSON.stringify({
      context,
      name: details.name,
      message: details.message.slice(0, 500),
      stack: details.stack?.slice(0, 4000),
      status: details.status,
      // Path only: query strings carry password-reset and verification tokens.
      url: window.location.origin + window.location.pathname,
      extra,
    });

    // sendBeacon outlives the page; a plain fetch is cut off by the navigation
    // or reload an error often triggers.
    if (!navigator.sendBeacon?.(REPORT_URL, body)) {
      void fetch(REPORT_URL, { method: "POST", body, keepalive: true }).catch(
        () => {},
      );
    }
  } catch {
    // Reporting must never break the page.
  }
}

/** Report uncaught errors and unhandled promise rejections. Client only. */
export function initErrorReporting(): void {
  if (typeof window === "undefined" || listening) return;
  listening = true;

  // Bubble phase on purpose: failed <img>/<script> loads only fire in the
  // capture phase, so this sees script errors alone.
  window.addEventListener("error", (event) => {
    if (event.filename && !isOwnUrl(event.filename)) return;
    reportError("WindowError", event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    // Our code only ever rejects with an Error. Anything else (a bare "404 "
    // string from an injected script) carries no stack to trace it by.
    if (!(event.reason instanceof Error)) return;
    reportError("UnhandledRejection", event.reason);
  });
}
